"use client";

import clsx from "clsx";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Accept, useDropzone } from "react-dropzone";
import { HiPlusCircle } from "react-icons/hi";

import { PdfView } from "components";
import CrossIconForUploader from "components/CrossIconForUploader/CrossIconForUploader";
import UploadErrorModal from "components/UploadErrorModal/UploadErrorModal";
import { TEST_IDS } from "constants/testIds";
import { VALIDATION_MESSAGE, VALIDATION_RULES } from "constants/validation";
import { getMimeType } from "utils/fileType.util";
import { AcceptFileType } from "utils/modalMessage.util";

const VALIDATION_CODE = {
  FILE_INVALID_TYPE: "file-invalid-type", // Dropzoneのデフォルトのエラーコード
  FILE_TOO_LARGE: "file-too-large", // カスタムのエラーコード
  TOO_MANY_FILES: "too-many-files", // カスタムのエラーコード
} as const;

export type SingleDocumentUploaderProps = {
  accept?: Accept;
  onFileChange: (file: File | null) => void;
  isError?: boolean;
  src?: string;
  onRemove?: () => void;
  isVisibleCancelButton?: boolean;
  maxFileSize?:
    | typeof VALIDATION_RULES.MAX_FILE_UPLOAD_SIZE
    | typeof VALIDATION_RULES.MAX_CONTRACT_PDF_SIZE
    | typeof VALIDATION_RULES.MAX_CONTRACT_PDF_SIZE_30MB;
  dataTestId?: string;
  isDisabled?: boolean;
  alignItems?: "start" | "center";
  onClick?: () => void;
  classUpload?: string;
  isForceRemove?: boolean;
  borderWidth?: string;
};

/**
 * 1つのファイルをアップロードするコンポーネント
 * 対応ファイル形式：JPG、PNG、PDF
 */
const SingleDocumentUploader = memo(
  ({
    accept = {
      "image/jpeg": [".jpg"],
      "image/png": [".png"],
      "application/pdf": [".pdf"],
    },
    onFileChange,
    isError = false,
    src,
    onRemove,
    isVisibleCancelButton = true,
    // 基本的にプロジェクト統一で20MBに制限していますが、外部 API の制約により一部例外あり
    maxFileSize = VALIDATION_RULES.MAX_FILE_UPLOAD_SIZE,
    dataTestId = TEST_IDS.SINGLE_DOCUMENT_UPLOADER.DEFAULT,
    isDisabled = false,
    alignItems,
    onClick,
    classUpload = "",
    isForceRemove = false,
    borderWidth = "border-2",
  }: SingleDocumentUploaderProps) => {
    const [files, setFiles] = useState<File[]>([]);
    const [previewUrl, setPreviewUrl] = useState(src);

    // propsで渡されたsrcが変更されたら、プレビューを更新する
    useEffect(() => {
      if (!src) {
        setPreviewUrl(undefined);
        setFiles([]);
        return;
      }
      setPreviewUrl(src);
    }, [src]);

    useEffect(() => {
      if (isForceRemove) {
        setPreviewUrl(undefined);
        setFiles([]);
      }
    }, [isForceRemove]);

    // ファイルのバリデーション
    const fileValidator = useCallback(
      (file: File) => {
        // ファイルサイズが maxFileSize 以下かどうか
        const isFileSizeValid = maxFileSize >= file.size;
        if (!isFileSizeValid) {
          return {
            code: VALIDATION_CODE.FILE_TOO_LARGE,
            message: {
              [VALIDATION_RULES.MAX_FILE_UPLOAD_SIZE]: VALIDATION_MESSAGE.MAX_FILE_UPLOAD_SIZE,
              [VALIDATION_RULES.MAX_CONTRACT_PDF_SIZE]: VALIDATION_MESSAGE.MAX_CONTRACT_PDF_SIZE,
              [VALIDATION_RULES.MAX_CONTRACT_PDF_SIZE_30MB]: VALIDATION_MESSAGE.MAX_CONTRACT_PDF_SIZE_30MB,
            }[maxFileSize],
          };
        }
        return null;
      },
      [maxFileSize]
    );

    // ファイルがドロップされた時の処理
    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        const isPdf = file.type === "application/pdf";

        setFiles([file]);
        isPdf ? setPreviewUrl(URL.createObjectURL(file)) : setPreviewUrl(undefined);
        onFileChange(file);
      },
      [onFileChange]
    );

    const { getRootProps, getInputProps, fileRejections } = useDropzone({
      onDrop,
      accept,
      multiple: false,
      validator: fileValidator,
    });

    // FILE_INVALID_TYPEの場合のみエラーモーダルを表示するためその判定
    // DOCS: https://docs.google.com/spreadsheets/d/1rMG3osqYUXwT9x_n6MlF2M3tCE2BY0Zhnc_vH7yfSBg/edit#gid=444086591
    const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);
    const isErrorInvalidType = useCallback(
      () => fileRejections.some(({ errors: [{ code }] }) => code === VALIDATION_CODE.FILE_INVALID_TYPE),
      [fileRejections]
    );
    useMemo(() => {
      if (isErrorInvalidType()) {
        setIsOpenErrorModal(true);
      }
    }, [isErrorInvalidType]);

    // ファイルを削除する時の処理
    const handleRemove = useCallback(
      (e: React.MouseEvent<SVGElement>) => {
        setFiles([]);
        setPreviewUrl(undefined);
        onFileChange(null);
        onRemove?.();
        e.stopPropagation();
      },
      [onFileChange, onRemove]
    );

    const borderClasses = isError
      ? `bg-[#fdf2e1] border-attention ${borderWidth}`
      : files.length > 0 || previewUrl
      ? `border-gray ${borderWidth}`
      : `border-gray border-dashed ${borderWidth}`;

    const [previewUrlSrcType, setPreviewUrlSrcType] = useState<
      "application/pdf" | "image/jpeg" | "image/png" | undefined
    >(undefined);

    useEffect(() => {
      previewUrl &&
        getMimeType(previewUrl, (type) => {
          setPreviewUrlSrcType(type);
        });
    }, [previewUrl]);

    // プレビューがある状態でアップロードできないように変更
    const uploaderDisabled = !previewUrl && { ...getRootProps() };

    const fileErrorHandler = (message: string, code: string) => {
      switch (code) {
        case VALIDATION_CODE.FILE_TOO_LARGE:
          return message;
        case VALIDATION_CODE.TOO_MANY_FILES:
          return VALIDATION_MESSAGE.TOO_MANY_FILES;
        default:
          return "予期せぬエラーが発生しました";
      }
    };

    return (
      <div
        className={clsx("flex flex-col", alignItems ? `items-${alignItems}` : null)}
        onClick={(e) => {
          if (onClick) {
            e.stopPropagation();
            onClick();
          }
        }}
      >
        <div
          {...uploaderDisabled}
          className={clsx(
            "relative flex h-[120px] w-[120px] cursor-pointer items-center justify-center bg-white",
            borderClasses,
            classUpload
          )}
        >
          <input
            {...getInputProps()}
            id="file"
            name="file"
            type="file"
            aria-label="file-select"
            data-testid={dataTestId}
            disabled={isDisabled}
          />
          {previewUrl ? (
            <>
              <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-white">
                {previewUrlSrcType === "application/pdf" ? (
                  <PdfView src={previewUrl} size={80} />
                ) : (
                  <embed
                    src={previewUrl}
                    width="100%"
                    height="100%"
                    className="pointer-events-none object-cover"
                    type={previewUrlSrcType}
                    key={previewUrlSrcType}
                  />
                )}
              </div>

              {isVisibleCancelButton && <CrossIconForUploader onClick={handleRemove} />}
            </>
          ) : files.length > 0 ? (
            <>
              {files[0].type === "application/pdf" ? (
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-white">
                  <PdfView src={previewUrl} size={80} />
                </div>
              ) : (
                <img
                  src={URL.createObjectURL(files[0])}
                  alt={`preview`}
                  className="absolute left-0 top-0 h-full w-full object-cover"
                />
              )}
              {isVisibleCancelButton && <CrossIconForUploader onClick={handleRemove} />}
            </>
          ) : (
            <HiPlusCircle size={30} fill="#706D65" />
          )}
        </div>
        {
          // FILE_TOO_LARGEの場合のみバリデーションメッセージを表示
          // DOCS: https://docs.google.com/spreadsheets/d/1rMG3osqYUXwT9x_n6MlF2M3tCE2BY0Zhnc_vH7yfSBg/edit#gid=444086591
          fileRejections
            .map(({ errors: [{ message, code }] }) => ({
              message,
              code,
            }))
            .map(
              ({ message, code }, index) =>
                code !== VALIDATION_CODE.FILE_INVALID_TYPE && (
                  <p key={index} className="mt-4 text-sm font-bold text-attention">
                    {fileErrorHandler(message, code)}
                  </p>
                )
            )
        }
        <UploadErrorModal
          isOpen={isOpenErrorModal}
          onClose={() => {
            setIsOpenErrorModal(false);
          }}
          acceptFileTypes={Object.keys(accept) as AcceptFileType[]}
        />
      </div>
    );
  }
);

SingleDocumentUploader.displayName = "SingleDocumentUploader";

export default SingleDocumentUploader;
